import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import {
  CardPanel,
  Card,
  IconHighlight,
  Button,
  Skeleton,
  WidgetEmptyErrorPlaceholder,
} from "@ann/web-components";
import { CardWrapper, ContentWrapper } from "./RecentActivity.styled";
import { RecentActivityContent } from "./components";
import ScrollableCardContent from "../ScrollableCardContent";
import { useActivitySnapshot } from "../../../../hooks/useActivitySnapshot";

const RecentActivity = observer(() => {
  const navigate = useNavigate();
  const { allItems, currentCollection, handleRefresh, isLoading, error } = useActivitySnapshot();
  const displayedItems = useMemo(() => allItems, [allItems]);
  const subtitle = `(${allItems.length})`;

  if (isLoading) {
    return <Skeleton height="428px" width="100%" />;
  }

  if (error) {
    return (
      <CardWrapper>
        <Card size='large'>
          <CardPanel isExpandable={false} openDefault={true}>
            <CardPanel.Content>
              <WidgetEmptyErrorPlaceholder
                buttonText="Retry"
                icon="error"
                message="We couldn't load recent activity. Please try again."
                onClick={handleRefresh}
                showButton
                title="Something went wrong"
              />
            </CardPanel.Content>
          </CardPanel>
        </Card>
      </CardWrapper>
    );
  }

  if (allItems.length === 0) {
    return (
      <CardWrapper>
        <Card size='large'>
          <CardPanel isExpandable={false} openDefault={true}>
            <CardPanel.Content>
              <WidgetEmptyErrorPlaceholder
                icon="lastUpdated"
                message="No recent activity to display."
                showButton={false}
                title="No Activity Found"
              />
            </CardPanel.Content>
          </CardPanel>
        </Card>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper>
      <Card size="large">
        <CardPanel isExpandable={false}>
          <CardPanel.Header subtitle={subtitle} title="Recent Activity">
            <CardPanel.Header.LeftSlot>
              <IconHighlight
                bgColor="Success"
                icon="lastUpdated"
                iconColor="Success"
                size="medium"
              />
            </CardPanel.Header.LeftSlot>
          </CardPanel.Header>
          <CardPanel.Content>
            <ScrollableCardContent>
              <ContentWrapper>
                <RecentActivityContent
                  items={displayedItems}
                  loading={currentCollection.loading}
                  error={currentCollection.error}
                  onRetry={handleRefresh}
                />
              </ContentWrapper>
            </ScrollableCardContent>
          </CardPanel.Content>
          <CardPanel.Actions>
            <Button
              label="View all"
              size="medium"
              type="secondary"
              onClick={() => {
                handleRefresh();
                navigate("../activity/all");
              }}
            />
          </CardPanel.Actions>
        </CardPanel>
      </Card>
    </CardWrapper>
  );
});

export default RecentActivity;
